/**
 * 地図画面 (`/map`) で受け取る URL クエリパラメータの zod スキーマ宣言。
 *
 * - 新しいパラメータを追加する場合は `MAP_URL_PARAM_SCHEMA` にキーと値スキーマを 1 行追加するだけ。
 * - `MapUrlParams` 型は `z.infer` でこの宣言から自動導出されるため、スキーマ変更が型に直接反映される。
 * - パース処理（`URLSearchParams` → このスキーマ）は `parseMapUrlParams.ts` を参照。
 */
import { z } from "zod";

import { CHIBAN_SEARCH_MAX_LENGTH } from "@/features/map/constants";
import {
  countGraphemes,
  normalizeSearchText,
} from "@/features/map/utils/sanitizeSearchText";

/**
 * URL クエリ値の正規化（制御文字・ゼロ幅を除去 + NFC 合成してから前後空白を trim する）。
 * `nullableString` と長さ超過判定（`isAddressParamTooLong`）で共有し、正規化のドリフトを防ぐ。
 * NFC 合成はフォーム入力（SearchBox）と揃え、NFD でコピーされた住所も検索一致させる。
 */
export const normalizeUrlParamText = (value: string): string =>
  normalizeSearchText(value).trim();

/**
 * `null` 許容文字列パラメータの値スキーマ。
 *
 * URL クエリは任意入力のため入口で正規化する:
 *   1. 制御文字（C0 / DEL / bidi / ゼロ幅）を除去（フォーム入力と共通の stripControlChars）
 *   2. 前後空白を除去（ゼロ幅文字に隣接した空白も確実に落とすため制御文字除去の後に trim する）
 *   3. 空（空白・制御文字のみ）なら `null` に正規化
 *   4. `maxLength` 超過は不正値として `null` に落とす（途中での切り捨てはしない）。
 *      上限は見た目の文字数で判定し、フォーム入力のカウンタ（SearchBox）と単位を揃える。
 *
 * 入力は未指定を空文字列に正規化した生の文字列（`parseMapUrlParams` 側で `?? ""`）。
 */
const nullableString = (
  maxLength: number
): z.ZodEffects<z.ZodString, string | null, string> =>
  z.string().transform((v) => {
    const cleaned = normalizeUrlParamText(v);
    return cleaned.length === 0 || countGraphemes(cleaned) > maxLength
      ? null
      : cleaned;
  });

/**
 * 厳密な真偽値パラメータの値スキーマ。
 * 文字列 `"true"` の厳密一致のみ `true`、それ以外（未指定含む）は `false`。
 */
const strictBoolean = z.string().transform((v) => v === "true");

/**
 * 地図画面 (`/map`) の URL クエリパラメータ宣言。
 *
 * 追加・削除・型変更はすべてこのオブジェクト 1 箇所で完結する。
 *
 * パラメータの組み合わせ可否・適用順:
 *   - `address` / `chibanchizu` / `requestedPicturesList` は相互排他ではなく、同時指定可能。
 *   - レイヤー反映は `computeUrlBasedCheckedLayers` で `chibanchizu` → `requestedPicturesList`
 *     の順に適用する（地番系レイヤー確定後に取得済み登記レイヤーを追加）。
 *   - `address` の自動検索は上記レイヤー反映後の予想 mapMode を用いて実行する。
 *   - `address` / `chiban` の自動検索は `address` を優先する（両方あれば `chiban` と
 *     `kaokuNumber` は使わない）。詳細は `resolveAutoSearchTarget` を参照。
 */
export const MAP_URL_PARAM_SCHEMA = z.object({
  /**
   * 住居表示。指定時は自動で逆引き検索を行う。
   * 文字数上限は画面のテキスト入力欄（ChibanViewer の SearchBox）と
   * `CHIBAN_SEARCH_MAX_LENGTH` で共有する。
   */
  address: nullableString(CHIBAN_SEARCH_MAX_LENGTH),
  /** 地番地図表示フラグ（真偽値）。検索する地番を表す `chiban` とは別物。 */
  chibanchizu: strictBoolean,
  /** 取得済み登記ピン留めフラグ。 */
  requestedPicturesList: strictBoolean,
  /** 役所資料（黒塗り）のピン表示フラグ（R.E.ASSIST の「地図で見る」から開く）。 */
  municipalDocuments: strictBoolean,
  /**
   * 検索する登記地番。指定時は自動で地番検索を行う（Salesforce SSO 引き継ぎ用途）。
   * 表示フラグの `chibanchizu`（真偽値）とは別物。文字数上限は `address` と同じ
   * `CHIBAN_SEARCH_MAX_LENGTH` を共有する。
   */
  chiban: nullableString(CHIBAN_SEARCH_MAX_LENGTH),
  /**
   * 検索する家屋番号（Salesforce SSO 引き継ぎ用途）。`chiban` が無い場合は家屋番号を
   * 地番と同じ検索語として使う。家屋番号からの地番変換は将来対応のため座標解決までは行わない
   * （詳細は `resolveAutoSearchTarget` 参照）。
   */
  kaokuNumber: nullableString(CHIBAN_SEARCH_MAX_LENGTH),
});

/**
 * 地図画面で受け取る URL クエリパラメータの解析結果型。
 * `MAP_URL_PARAM_SCHEMA` から自動導出されるため、スキーマ変更が型に直接反映される。
 */
export type MapUrlParams = z.infer<typeof MAP_URL_PARAM_SCHEMA>;
